// ============================================================================
// AddChatMemberDialog — диалог «Добавить участника» в групповой чат (§2.3).
// Единый механизм: ContactPicker (SSoT выбора контактов) + функция ядра
// chatBridge.addConversationMember (права/событие/уведомление — сервер).
// Список — люди (peopleOnly; системные учётки/боты скрыты сервером),
// уже состоящие в чате и сам пользователь исключены.
// ============================================================================

import { useEffect, useState } from 'react';
import type { Conversation } from '@zdc/sdk';
import { getSDK } from '@/store/sdkStore';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ContactPicker, type Contact } from '@/components/chat/ContactPicker';
import { addConversationMember } from '@/lib/chatBridge';
import { friendlyError } from '@/lib/errors';
import { filterPeople } from '@/lib/people';
import { createLogger } from '@/lib/logger';
import { useToastStore } from '@/store/toastStore';

const log = createLogger('AddChatMemberDialog');

interface AddChatMemberDialogProps {
  open: boolean;
  onClose: () => void;
  conversationId: string;
  /** Кого не показывать (уже участники + сам пользователь). */
  excludeIds: string[];
  /** Успешное добавление: актуальный диалог с сервера (SSoT). */
  onAdded: (conv: Conversation | null) => void;
  /** Заголовок окна (по умолчанию «Добавить участника»). */
  title?: string;
  /** Подпись кнопки действия (по умолчанию «Добавить в группу»). */
  submitLabel?: string;
}

export function AddChatMemberDialog({
  open, onClose, conversationId, excludeIds, onAdded,
  title = 'Добавить участника', submitLabel = 'Добавить в группу',
}: AddChatMemberDialogProps) {
  const [people, setPeople] = useState<Contact[]>([]);
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Контакты грузятся ЗАНОВО при каждом открытии (новый пользователь виден
  // без перезахода); peopleOnly — серверный фильтр «только люди».
  useEffect(() => {
    if (!open) return;
    let alive = true;
    setTarget('');
    setError('');
    getSDK().entity
      .listEntities({ peopleOnly: true })
      .then((r) => {
        if (!alive) return;
        const items = ((r as { items?: unknown[] }).items || []) as Array<Record<string, unknown>>;
        setPeople(filterPeople(items).map((u) => ({
          id: String(u.id || ''),
          name: String(u.name || u.login || u.id || ''),
          avatarUrl: String(u.avatarUrl || u.avatar_url || ''),
          subtitle: String(u.login || u.email || ''),
          login: String(u.login || ''),
          kind: String(u.kind || (u.isBot ? 'bot' : '')),
        })));
      })
      .catch((e: unknown) => {
        log.warn('список людей не загружен', { error: String((e as Error)?.message || e) });
        if (alive) setError('Не удалось загрузить список пользователей');
      });
    return () => { alive = false; };
  }, [open]);

  const handleAdd = async () => {
    if (!target) return;
    setBusy(true);
    setError('');
    try {
      const conv = await addConversationMember(conversationId, target);
      onAdded(conv);
      useToastStore.getState().showSuccess('Участник добавлен в группу');
      onClose();
    } catch (e) {
      log.error('не удалось добавить участника', e);
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <UniversalWindow
      open={open}
      onClose={onClose}
      title={title}
      blocking
      width="max-w-md"
      height="h-[min(560px,85vh)]"
      resizable={false}
    >
      <div className="p-3 space-y-2 flex flex-col h-full">
        <div className="flex-1 min-h-0 flex flex-col">
          <Label className="text-[10px] text-muted-foreground">Пользователи, не состоящие в группе</Label>
          <div className="mt-1 flex-1 min-h-0">
            <ContactPicker
              contacts={people}
              excludeIds={excludeIds}
              selected={target ? [target] : []}
              onSelect={(ids) => setTarget(ids[0] || '')}
              single
              placeholder="Поиск участника…"
              listClassName="h-full"
            />
          </div>
        </div>
        {error && <div className="text-[10px] text-destructive">{error}</div>}
        <Button
          size="sm"
          className="w-full h-7 text-[11px] shrink-0"
          disabled={!target || busy}
          onClick={() => void handleAdd()}
        >
          {busy ? 'Добавление…' : submitLabel}
        </Button>
      </div>
    </UniversalWindow>
  );
}
